/**
 * DocumentSetsRequest Selectors
 */
import { createSelectorCreator, defaultMemoize } from 'reselect';
import isEqual from 'lodash.isequal';

// Selectors
import { selectCurrentEntityId } from '../../../../services/currentEntityId/selectors';
import { selectCurrentSeasonId } from '../../../../services/currentSeasonId/selectors';
import { makeSelectQueryStringValue } from '../../../../workspaces/react-router-selectors';

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

const makeSelectParameters = () =>
    createDeepEqualSelector(
        [
            selectCurrentSeasonId,
            selectCurrentEntityId,
            makeSelectQueryStringValue('page'),
            makeSelectQueryStringValue('count'),
            makeSelectQueryStringValue('search'),
            makeSelectQueryStringValue('sort'),
            makeSelectQueryStringValue('schemas'),
            makeSelectQueryStringValue('users'),
            makeSelectQueryStringValue('varieties'),
            makeSelectQueryStringValue('groups')
        ],
        (
            currentSeasonIdState,
            currentEntityIdState,
            queryStringPageState = 1,
            queryStringCountState = 25,
            queryStringSearchState,
            queryStringSortState = 'createdAt_descending',
            queryStringSchemasState,
            queryStringUsersState,
            queryStringVarietiesState,
            queryStringGroupsState
        ) => ({
            filter: {
                limit: queryStringCountState,
                offset: (queryStringPageState - 1) * queryStringCountState,
                order: queryStringSortState
                    .split('_')
                    .map((value) => {
                        if (value === 'descending') return 'desc';
                        if (value === 'ascendingDictionary') return '';
                        return value;
                    })
                    .join(' '),
                where: {
                    'documentSetAttributes.isVisible': true
                },
                require: {
                    'event.schema.eventTypeId': { $exists: false },
                    'archivedAt': { $exists: false },
                    $and: [
                        { deletedAt: { $exists: false } },
                        // Schemas
                        queryStringSchemasState &&
                            queryStringSchemasState.length && {
                                'event.schema.name': {
                                    $in: queryStringSchemasState
                                }
                            },
                        // Varieties
                        queryStringVarietiesState &&
                            queryStringVarietiesState.length && {
                                'event.entity.defaultVarietyId': {
                                    $in: queryStringVarietiesState
                                }
                            },
                        // Groups
                        queryStringGroupsState &&
                            queryStringGroupsState.length && {
                                'event.groupId': {
                                    $in: queryStringGroupsState
                                }
                            },
                        // Season
                        currentSeasonIdState && {
                            'seasonId': currentSeasonIdState
                        },
                        // Entity
                        currentEntityIdState && {
                            $or: [
                                {
                                    'event.entity.path': {
                                        $or: [
                                            {
                                                $like: `/${currentEntityIdState}/%`
                                            },
                                            {
                                                $equals: `/${currentEntityIdState}`
                                            }
                                        ]
                                    }
                                },
                                {
                                    'event.entityId': currentEntityIdState
                                }
                            ]
                        },
                        // Users
                        queryStringUsersState &&
                            queryStringUsersState.length && {
                                'assignedUsers.email': {
                                    $in: queryStringUsersState
                                }
                            }
                    ].filter((value) => value)
                }
            },
            search: queryStringSearchState
        })
    );

export { makeSelectParameters };
